import { jwtDecode } from 'jwt-decode';

// Ponto único de leitura/gravação da sessão no navegador. Os tokens ficam no localStorage porque o
// backend só aceita o Bearer na maior parte das rotas; quando ele aceitar cookie HttpOnly em todas,
// a troca de armazenamento começa por aqui.
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';
const LOGIN_TIME_KEY = 'loginTime';
const TOKEN_EXPIRATION_KEY = 'tokenExpiration';
const ACTIVE_UNIT_KEY = 'activeUnitId';
const LAST_ACTIVITY_KEY = 'lastActivityAt';
const SESSION_END_REASON_KEY = 'siapen:session_end_reason';

// A sessão termina (ou o token é renovado) esse tempo antes de o token de acesso vencer: depois de
// vencido, o servidor já recusa o token.
export const SESSION_END_MARGIN_SECONDS = 5 * 60;

// Dados pessoais do login que nenhuma tela usa: não ficam guardados no navegador.
// (CPF continua porque a tela de perfil exibe.)
const UNUSED_PERSONAL_USER_FIELDS = [
  'rg',
  'date_of_birth',
  'gender',
  'sexual_orientation',
  'color_or_ethnicity',
  'marital_status',
  'social_name',
  'nationality',
  'naturalness',
  'nationality_country',
  'naturalness_city',
];

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

// `exp` do JWT em segundos; null se não der pra decodificar.
export const getTokenExp = (token) => {
  if (!token) return null;
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === 'number' ? exp : null;
  } catch (error) {
    return null;
  }
};

// Vencido (ou vence dentro de `marginSeconds`). Token sem `exp` legível conta como vencido.
export const isTokenExpired = (token, marginSeconds = 0) => {
  const exp = getTokenExp(token);
  if (!exp) return true;
  return exp <= Date.now() / 1000 + marginSeconds;
};

// O refresh só estende a sessão se vencer depois do token de acesso atual. Hoje os dois vencem juntos
// (3h após o login): não há renovação e a sessão termina SESSION_END_MARGIN_SECONDS antes do vencimento.
export const canRenewAccessToken = () => {
  const accessExp = getTokenExp(getAccessToken());
  const refreshToken = getRefreshToken();
  const refreshExp = getTokenExp(refreshToken);
  if (!accessExp || !refreshExp || isTokenExpired(refreshToken)) return false;
  return refreshExp > accessExp + 60;
};

// Sessão encerrada: sem token, ou faltando SESSION_END_MARGIN_SECONDS (ou menos) para o token vencer
// sem ter como renovar.
export const isSessionOver = () => {
  const accessToken = getAccessToken();
  if (!accessToken) return true;
  return isTokenExpired(accessToken, SESSION_END_MARGIN_SECONDS) && !canRenewAccessToken();
};

// Última atividade do usuário em qualquer aba: a inatividade vale para o navegador inteiro.
export const touchLastActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now());
};

export const getLastActivityAt = () => {
  const value = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
  return Number.isFinite(value) && value > 0 ? value : null;
};

// Motivo do fim automático da sessão ('inactivity' | 'expired' | 'ended'), pra tela de login avisar.
// Vai pro sessionStorage da aba (sobrevive ao reload do interceptor) e pro localStorage (as outras abas,
// que saem junto, leem o mesmo motivo). Gravar ANTES de limpar a sessão.
export const setSessionEndReason = (reason) => {
  sessionStorage.setItem(SESSION_END_REASON_KEY, reason);
  localStorage.setItem(SESSION_END_REASON_KEY, reason);
};

// Motivo gravado pela aba que encerrou a sessão (null se foi pelo botão "Sair").
export const getSharedSessionEndReason = () => localStorage.getItem(SESSION_END_REASON_KEY);

// Lido uma vez pela tela de login: devolve o motivo e apaga, pro aviso não se repetir.
export const consumeSessionEndReason = () => {
  const reason = sessionStorage.getItem(SESSION_END_REASON_KEY);
  sessionStorage.removeItem(SESSION_END_REASON_KEY);
  return reason;
};

export const sanitizeStoredUser = (user) => {
  if (!user || typeof user !== 'object') return user;
  const sanitized = { ...user };
  UNUSED_PERSONAL_USER_FIELDS.forEach((field) => delete sanitized[field]);
  return sanitized;
};

// Grava o par de tokens. O refresh só é trocado quando a API devolve um novo (rotação).
export const saveTokens = ({ access, refresh }) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, access);
  const exp = getTokenExp(access);
  if (exp) localStorage.setItem(TOKEN_EXPIRATION_KEY, exp);
  if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
};

export const saveSession = ({ access, refresh, user }) => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(SESSION_END_REASON_KEY);
  sessionStorage.removeItem(SESSION_END_REASON_KEY);
  saveTokens({ access, refresh });
  localStorage.setItem(USER_KEY, JSON.stringify(sanitizeStoredUser(user)));
  localStorage.setItem(LOGIN_TIME_KEY, new Date().getTime());
  touchLastActivity();
};

export const clearSession = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(LOGIN_TIME_KEY);
  localStorage.removeItem(TOKEN_EXPIRATION_KEY);
  localStorage.removeItem(ACTIVE_UNIT_KEY);
  localStorage.removeItem(LAST_ACTIVITY_KEY);
};
